import {
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { EntityForm } from "../EntityForm";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { getEquipments } from "../../api/equipment";
import {
  createEquipmentPreCheckQxn,
  getEquipmentPreCheckQxn,
  updateEquipmentPreCheckQxn,
} from "../../api/equipmentPreCheckQxn";
import {
  createEquipmentPreCheckAns,
  updateEquipmentPreCheckAns,
} from "../../api/equipmentPreCheckAns";
import { MultiTextInput } from "./multiTextInput";

export const QuestionType = {
  Select: 1,
  MultiSelect: 2,
  Text: 3,
};

export class PreCheckQxn {
  constructor(type, text, description, options) {
    this.type = type;
    this.text = text;
    this.description = description;
    this.options = options;
  }
}

export function PrecheckQxnRenderer({ ansSet, user, onSubmit, onCancel }) {
  const [equipments, setEquipments] = useState([]);
  const [equipment, setEquipment] = useState(null);
  const [precheckQxn, setPrecheckQxn] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchEquipments = async ({ page, q }) => {
    try {
      const { results } = await getEquipments({ page, perPage: 5, q });
      setEquipments(results);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  const handleSubmit = (submitData) => {
    const answers = precheckQxn.questions.map((q, i) => {
      const value = submitData[i];
      const answer = {
        question: q,
        value,
      };

      return answer;
    });

    setIsSubmitting(true);
    if (!ansSet) {
      const ans = {
        answers,
        questionSet: precheckQxn,
        user,
      };
      createEquipmentPreCheckAns(ans)
        .then(() => {
          setIsSubmitting(false);
          onSubmit();
        })
        .catch(() => setIsSubmitting(false));
    } else {
      const ans = {
        ...ansSet,
        answers,
      };
      updateEquipmentPreCheckAns(ans)
        .then(() => {
          setIsSubmitting(false);
          onSubmit();
        })
        .catch(() => setIsSubmitting(false));
    }
  };

  const sections = useMemo(() => {
    if (!precheckQxn || !precheckQxn.questions) return;
    return precheckQxn.questions.map((q, index) => {
      const entityItem = (() => {
        switch (q.type) {
          case QuestionType.Select:
          case QuestionType.MultiSelect:
            return {
              type: "async-select",
              label: q.text,
              additionalInfo: q.description,
              name: index,
              required: true,
              placeholder: "select option",
              mode: q.type === QuestionType.MultiSelect ? "multiple" : "tag",
              options: q.options.map((o) => {
                return {
                  value: o,
                  label: o,
                };
              }),
            };
          default: {
            return {
              type: "text",
              label: q.text,
              additionalInfo: q.description,
              name: index,
              required: true,
            };
          }
        }
      })();

      return { items: [entityItem] };
    });
  }, [precheckQxn, ansSet]);

  useEffect(() => {
    if (!equipment || ansSet) return;
    const fetchPrecheckQxn = async () => {
      const { results } = await getEquipmentPreCheckQxn({
        equipmentId: equipment.id,
      });
      if (!results.length) return;
      const precheckQxn = results[0];

      setPrecheckQxn(precheckQxn);
    };

    fetchPrecheckQxn();
  }, [equipment]);

  useEffect(() => {
    if (!ansSet) return;
    setEquipment(ansSet.questionSet.equipment);
    setPrecheckQxn({ questions: ansSet.answers.map((a) => a.question) });
  }, [ansSet]);

  if (!equipment)
    return (
      <Modal
        title="Precheck Questions Form"
        width={1000}
        okButtonProps={{ style: { display: "none" } }}
        open
      >
        <Space direction="vertical">
          <Card type="inner">
            <Space direction="vertical">
              <Typography.Text>Related Equipment</Typography.Text>
              <Select
                value={equipment ? equipment.id : null}
                placeholder="Search Equipment"
                onSearch={(str) => fetchEquipments({ page: 1, q: str })}
                size="large"
                onChange={(eid) => {
                  setEquipment(equipments.find((e) => e.id === eid));
                }}
                filterOption={false}
                options={equipments.map((e) => {
                  return {
                    value: e.id,
                    label: e.name,
                  };
                })}
                showSearch
              ></Select>
            </Space>
          </Card>
        </Space>
      </Modal>
    );

  if (equipment && precheckQxn && precheckQxn.questions)
    return (
      <EntityForm
        title={`Precheck for ${equipment.name}`}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        sections={sections}
        initialValues={
          ansSet
            ? ansSet.answers.reduce((acc, a, i) => {
              acc[i] = a.value;
              return acc;
            }, {})
            : {}
        }
        open
        isEditMode={!!ansSet}
      />
    );

  return "loading..";
}

function QxnGeneratorElement({ qxn, onDone, onCancel }) {
  const [qxnStr, setQxnStr] = useState("");
  const [qxnType, setQxnType] = useState(null);
  const [options, setOptions] = useState([]);

  const isTypeSelectorEnabled = !!qxnStr.length;
  const isAddBtnEnabled = isTypeSelectorEnabled && qxnType;

  const clear = () => {
    setQxnStr("");
    setQxnType(null);
    setOptions([]);
  };

  const handleDoneBtnClick = () => {
    onDone(new PreCheckQxn(qxnType, qxnStr, null, options));
    clear();
  };

  const handleCancelBtnClick = () => {
    onCancel();
    clear();
  };

  useEffect(() => {
    if (!qxn) return;

    setQxnStr(qxn.text);
    setQxnType(qxn.type);
    setOptions(qxn.options);
  }, [qxn]);

  return (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        <Space.Compact block style={{ width: "100%" }}>
          <Input
            value={qxnStr}
            placeholder="Type question here"
            size="large"
            style={{ width: "100%" }}
            onChange={(e) => setQxnStr(e.target.value)}
          ></Input>
          <Select
            value={qxnType}
            disabled={!isTypeSelectorEnabled}
            size="large"
            placeholder="Select Question type"
            onChange={(e) => setQxnType(e)}
            options={[
              { value: QuestionType.Text, label: "Text answer" },
              { value: QuestionType.Select, label: "Select Options" },
              {
                value: QuestionType.MultiSelect,
                label: "Multi-Select Options",
              },
            ]}
          />
        </Space.Compact>

        {(qxnType === QuestionType.Select ||
          qxnType === QuestionType.MultiSelect) && (
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                Options to choose from the questions.
              </Typography.Text>
              <MultiTextInput onChange={(e) => setOptions(e)} options={options} />
            </Space>
          )}

        <Space>
          <Button
            size="large"
            type="primary"
            disabled={!isAddBtnEnabled}
            onClick={handleDoneBtnClick}
          >
            Done
          </Button>
          {onCancel && (
            <Button size="large" onClick={handleCancelBtnClick}>
              Cancel
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
}

function QxnRendererElement({ qxn, onEdit }) {
  const [isEdit, setIsEdit] = useState(false);

  const qxnTagStr = (() => {
    switch (qxn.type) {
      case QuestionType.Select:
        return "Select Options";
      case QuestionType.MultiSelect:
        return "Multi-Select Options";
      default:
        return "Text answer";
    }
  })();

  if (isEdit)
    return (
      <QxnGeneratorElement
        qxn={qxn}
        onDone={(qxn) => {
          onEdit(qxn);
          setIsEdit(false);
        }}
        onCancel={() => setIsEdit(false)}
      />
    );

  return (
    <Card type="inner">
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Space direction="vertical">
          <Tag color="#2db7f5">{qxnTagStr}</Tag>
          <Typography.Title level={5}>{qxn.text}</Typography.Title>
        </Space>

        <Space size={4} align="baseline">
          <Button icon={<DeleteOutlined />} size="large" />
          <Button
            icon={<EditOutlined />}
            size="large"
            onClick={() => setIsEdit(true)}
          />
        </Space>
      </Space>
    </Card>
  );
}

export function PrecheckQxnGenerator({
  initialValues,
  precheckQxnEquipment,
  onSubmit,
  onCancel,
}) {
  const [qxns, setQxns] = useState([]);
  const [precheckQxnSetId, setPrecheckQxnSetId] = useState(null);

  const [equipments, setEquipments] = useState([]);
  const [equipment, setEquipment] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchEquipments = async ({ page, q }) => {
    try {
      const { results } = await getEquipments({ page, perPage: 5, q });
      setEquipments(results);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    if (!precheckQxnSetId) {
      createEquipmentPreCheckQxn({ equipment, questions: qxns })
        .then(() => {
          setIsSubmitting(false);
          onSubmit();
        })
        .catch(() => setIsSubmitting(false));
    } else {
      updateEquipmentPreCheckQxn({
        id: precheckQxnSetId,
        equipment,
        questions: qxns,
      })
        .then(() => {
          setIsSubmitting(false);
          onSubmit();
        })
        .catch(() => setIsSubmitting(false));
    }
  };

  useEffect(() => {
    if (initialValues && initialValues.questions) {
      setQxns(initialValues.questions);
    }
  }, [initialValues])

  useEffect(() => {
    if (!equipment) return;
    const fetchPrecheckQxn = async () => {
      const { results } = await getEquipmentPreCheckQxn({
        equipmentId: equipment.id,
      });
      if (!results.length) return;
      const precheckQxn = results[0];

      setQxns(precheckQxn.questions);
      setPrecheckQxnSetId(precheckQxn.id);
    };

    fetchPrecheckQxn();
  }, [equipment]);

  useEffect(() => {
    if (!precheckQxnEquipment) return;
    setEquipments([precheckQxnEquipment]);
    setEquipment(precheckQxnEquipment);
  }, [precheckQxnEquipment]);

  return (
    <Modal
      title="Precheck Questions Form"
      width={1000}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      open
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Card type="inner">
          <Space direction="vertical">
            <Typography.Text>Related Equipment</Typography.Text>
            <Select
              value={equipment ? equipment.id : null}
              placeholder="Search Equipment"
              onSearch={(str) => fetchEquipments({ page: 1, q: str })}
              size="large"
              onChange={(eid) => {
                setEquipment(equipments.find((e) => e.id === eid));
              }}
              filterOption={false}
              options={equipments.map((e) => {
                return {
                  value: e.id,
                  label: e.name,
                };
              })}
              showSearch
            ></Select>
          </Space>
        </Card>

        {(equipment || initialValues) && (
          <>
            {qxns?.map((qxn, index) => (
              <QxnRendererElement
                qxn={qxn}
                onEdit={(qxn) => {
                  const questions = [...qxns];
                  questions[index] = qxn;
                  setQxns(questions);
                }}
              />
            ))}
            <QxnGeneratorElement onDone={(qxn) => setQxns([...qxns, qxn])} />
          </>
        )}

      </Space>
    </Modal>
  );
}
